import React, { useEffect, useState, useRef } from 'react';
import './CreateTasksForLead.css';

function CreateTasksForLead() {
  const [selectedLead, setSelectedLead] = useState(null);
  const [lastTriggeredLeadId, setLastTriggeredLeadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [lambdaResult, setLambdaResult] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  // Search input and autocomplete list state
  const [search, setSearch] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadSelected, setLeadSelected] = useState(false);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const containerRef = useRef(null);
  const isMounted = useRef(false);

  const fetchLeads = async (searchParam, abortSignal) => {
    if (!searchParam || searchParam.length < 3) {
      setLeads([]);
      return;
    }
    setLoadingLeads(true);
    try {
      const resp = await fetch(
        `/api/v1/leads/?page=1&page_size=12&status=In%20Progress&search=${encodeURIComponent(searchParam)}`,
        { signal: abortSignal }
      );
      if (resp.ok) {
        const data = await resp.json();
        const leadsList = Array.isArray(data) ? data : (data.results || []);
        setLeads(leadsList);
      } else {
        setLeads([]);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to fetch leads:', err);
      setLeads([]);
    }
    setLoadingLeads(false);
  };

  useEffect(() => {
    isMounted.current = true;
    
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowLeadDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      isMounted.current = false;
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounced search trigger (similar to Leads.js)
  useEffect(() => {
    if (!isMounted.current) return;
    if (leadSelected) return;

    if (!search || search.length < 2) {
      setLeads([]);
      setShowLeadDropdown(false);
      return;
    }

    const controller = new AbortController();

    const t = setTimeout(() => {
      fetchLeads(search, controller.signal);
      setShowLeadDropdown(true);
    }, 400);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [search, leadSelected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    setLoading(true);
    setSuccess('');
    setError('');
    setInfoMessage('');
    setLambdaResult(null);

    try {
      const resp = await fetch('/api/trigger-scoring-for-lead/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadId: selectedLead.id,
        })
      });

      const data = await resp.json();
      if (resp.ok) {
        if (data.success) {
          setSuccess(data.data?.message || 'Scoring API triggered successfully!');
          setLambdaResult(data.data || null);
          // Lock the button until a different lead is selected
          setLastTriggeredLeadId(selectedLead.id);
        } else {
          // No scrapped jobs found in last 2 days
          setInfoMessage(data.message || 'Scoring API not triggered.');
        }
      } else {
        setError(data.error || data.message || 'Failed to trigger scoring.');
      }
    } catch (err) {
      setError('Failed to trigger scoring API due to a network error.');
    }
    setLoading(false);
  };

  return (
    <div className="main-content">
      <h1 className="main-title">Trigger Scoring for Lead</h1>
      <form className="tasks-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <label className="form-label">Search In-Progress Lead</label>
          <div className="form-desc">
            Type to search by name or email, select a lead from the suggestions, and trigger the scoring API.
          </div>

          {/* Autocomplete Input */}
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setLeadSelected(false);
                // Always clear selected lead when user edits the input
                // This prevents triggering a stale lead after typing
                setSelectedLead(null);
                setLastTriggeredLeadId(null);
              }}
              onFocus={() => {
                if (search.length >= 2) {
                  setShowLeadDropdown(true);
                }
              }}
              placeholder="Type to search name, email..."
              className="form-input"
              style={{ width: '100%', paddingLeft: '2.5rem' }}
            />
            <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '12px', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {showLeadDropdown && (
              <div style={{
                position: 'absolute',
                zIndex: 50,
                marginTop: '0.25rem',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                maxHeight: '250px',
                overflowY: 'auto'
              }}>
                {loadingLeads ? (
                  <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Searching...
                  </div>
                ) : leads.length === 0 ? (
                  <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>No leads found</div>
                ) : (
                  leads.map(lead => {
                    const assocText = lead.assigned_associate ? ` | Assigned: ${lead.assigned_associate.name}` : '';
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => {
                          setSelectedLead(lead);
                          setSearch(`${lead.name} (${lead.email})`);
                          setLeadSelected(true);
                          setShowLeadDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.6rem 0.75rem',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{lead.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {lead.email} - {lead.targetRoleName || 'No Role'}{assocText}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {selectedLead && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#047857', fontWeight: 600 }}>
              ✓ Selected Lead: {selectedLead.name} (Role: {selectedLead.targetRoleName || 'No Role'}){selectedLead.assigned_associate ? ` | Assigned CA: ${selectedLead.assigned_associate.name}` : ''}
            </div>
          )}
        </div>

        <div className="form-section">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading || !selectedLead || selectedLead?.id === lastTriggeredLeadId}
          >
            {loading ? 'Triggering...' : 'Trigger Scoring API'}
          </button>
          {success && <div className="success-msg">{success}</div>}
          {infoMessage && <div className="info-msg">{infoMessage}</div>}
          {error && <div className="error-msg">{error}</div>}
        </div>
      </form>

      {lambdaResult && (
        <div className="lead-details-result" style={{ marginTop: '2.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#22223b', marginBottom: '1rem' }}>Lambda Trigger Details</h2>
          <table className="result-table">
            <tbody>
              <tr>
                <th>Message</th>
                <td>{lambdaResult.message}</td>
              </tr>
              {lambdaResult.ssm_command_id && (
                <tr>
                  <th>SSM Command ID</th>
                  <td><code>{lambdaResult.ssm_command_id}</code></td>
                </tr>
              )}
              {lambdaResult.ec2_instance_id && (
                <tr>
                  <th>EC2 Instance ID</th>
                  <td><code>{lambdaResult.ec2_instance_id}</code></td>
                </tr>
              )}
              {lambdaResult.command && (
                <tr>
                  <th>Command Executed</th>
                  <td>
                    <pre>{lambdaResult.command}</pre>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CreateTasksForLead;



